import { useId, useState, type FormEvent } from "react";
import { Banner, Button, Combobox, Field, Input, Textarea, type ComboboxOption } from "@optymyse/suite-ui";
import type { Company } from "../../../shared/types/directory";
import type { CompanyAddress } from "../../../shared/types/link";
import { COMPANY_LIMITS } from "../../../shared/types/link";
import type { CompanyBody, CompanyChangeBody } from "../../lib/directory";
import { COUNTRY_CODES, countryName } from "../../lib/countries";
import { filled, notesOf } from "../../lib/format";
import { strings } from "../../lib/strings";

/** A customer as the form holds it while it is typed: every field as text. */
export type CompanyDraft = {
  display_name: string;
  legal_name: string;
  domain: string;
  website: string;
  line1: string;
  line2: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
  notes: string;
};

const ADDRESS_FIELDS = ["line1", "line2", "city", "region", "postcode"] as const;

/** The form's start: the customer as read, or nothing for a new one. */
export function draftOf(company?: Company): CompanyDraft {
  const address = company?.address ?? null;
  return {
    display_name: company?.display_name ?? "",
    legal_name: company?.legal_name ?? "",
    domain: company?.domain ?? "",
    website: company?.website ?? "",
    line1: address?.line1 ?? "",
    line2: address?.line2 ?? "",
    city: address?.city ?? "",
    region: address?.region ?? "",
    postcode: address?.postcode ?? "",
    country: address?.country ?? "",
    notes: company?.notes ?? "",
  };
}

/** The address the form holds, or null when it holds none. */
function addressOf(draft: CompanyDraft): CompanyAddress | null {
  const address: CompanyAddress = {
    line1: filled(draft.line1),
    line2: filled(draft.line2),
    city: filled(draft.city),
    region: filled(draft.region),
    postcode: filled(draft.postcode),
    country: draft.country === "" ? null : draft.country,
  };
  return Object.values(address).every((part) => part === null) ? null : address;
}

/** Whether two addresses are the same, part by part. */
function sameAddress(a: CompanyAddress | null, b: CompanyAddress | null): boolean {
  if (a === null || b === null) return a === b;
  return (["line1", "line2", "city", "region", "postcode", "country"] as const).every((part) => a[part] === b[part]);
}

/** A new customer from what was typed. */
export function bodyOf(draft: CompanyDraft): CompanyBody {
  return {
    display_name: draft.display_name.trim(),
    legal_name: filled(draft.legal_name),
    domain: filled(draft.domain),
    website: filled(draft.website),
    address: addressOf(draft),
    notes: notesOf(draft.notes),
  };
}

/** What changed from the customer as read: each field that differs, null where it was cleared. */
export function changeOf(company: Company, draft: CompanyDraft): CompanyChangeBody {
  const now = bodyOf(draft);
  const change: CompanyChangeBody = {};
  if (now.display_name !== company.display_name) change.display_name = now.display_name;
  if (now.legal_name !== company.legal_name) change.legal_name = now.legal_name ?? null;
  if (now.domain !== company.domain) change.domain = now.domain ?? null;
  if (now.website !== company.website) change.website = now.website ?? null;
  if (!sameAddress(now.address ?? null, company.address ?? null)) change.address = now.address ?? null;
  if (now.notes !== company.notes) change.notes = now.notes ?? null;
  return change;
}

const COUNTRY_OPTIONS: readonly ComboboxOption[] = [
  { value: "", label: strings.customers.form.noCountry },
  ...COUNTRY_CODES.map((code) => ({ value: code, label: countryName(code), keywords: [code] })).sort((a, b) =>
    a.label.localeCompare(b.label, "en-GB"),
  ),
];

export type FormProblem = { readonly message: string; readonly field: string | null };

/**
 * A customer's fields, for a new one or a change: its name, legal name, domain and website, its postal address, and
 * notes, kept as written, line breaks and all. What it sends is the page's to say (`onSubmit`).
 */
export function CompanyForm({
  initial,
  submitLabel,
  pending,
  problem,
  onSubmit,
  onCancel,
}: {
  initial: CompanyDraft;
  submitLabel: string;
  pending: boolean;
  /** What the last try was refused for, said at its field where it names one. */
  problem: FormProblem | null;
  onSubmit: (draft: CompanyDraft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [nameMissing, setNameMissing] = useState(false);
  const addressHeading = useId();
  const set = (field: keyof CompanyDraft) => (value: string) => setDraft((was) => ({ ...was, [field]: value }));
  const fieldProblem = (field: string) => (problem?.field === field ? problem.message : undefined);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (draft.display_name.trim() === "") {
      setNameMissing(true);
      return;
    }
    setNameMissing(false);
    onSubmit(draft);
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-6">
      {problem !== null && problem.field === null && <Banner tone="negative">{problem.message}</Banner>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={strings.customers.form.displayName}
          hint={strings.customers.form.displayNameHint}
          error={nameMissing ? strings.customers.form.required : fieldProblem("display_name")}
          required
          className="sm:col-span-2"
        >
          <Input
            value={draft.display_name}
            maxLength={COMPANY_LIMITS.display_name}
            autoComplete="off"
            onChange={(event) => set("display_name")(event.target.value)}
          />
        </Field>
        <Field label={strings.customers.form.legalName} error={fieldProblem("legal_name")}>
          <Input
            value={draft.legal_name}
            maxLength={COMPANY_LIMITS.legal_name}
            autoComplete="off"
            onChange={(event) => set("legal_name")(event.target.value)}
          />
        </Field>
        <Field
          label={strings.customers.form.domain}
          hint={strings.customers.form.domainHint}
          error={fieldProblem("domain")}
        >
          <Input
            value={draft.domain}
            maxLength={COMPANY_LIMITS.domain}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => set("domain")(event.target.value)}
          />
        </Field>
        <Field label={strings.customers.form.website} error={fieldProblem("website")} className="sm:col-span-2">
          <Input
            type="url"
            value={draft.website}
            maxLength={COMPANY_LIMITS.website}
            autoComplete="off"
            spellCheck={false}
            placeholder="https://"
            onChange={(event) => set("website")(event.target.value)}
          />
        </Field>
      </div>

      <fieldset aria-labelledby={addressHeading} className="flex flex-col gap-4">
        <h2 id={addressHeading} className="text-heading">
          {strings.customers.form.address}
        </h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {ADDRESS_FIELDS.map((field) => (
            <Field
              key={field}
              label={strings.customers.form[field]}
              className={field === "line1" || field === "line2" ? "sm:col-span-2" : undefined}
            >
              <Input
                value={draft[field]}
                maxLength={field === "postcode" ? COMPANY_LIMITS.postcode : COMPANY_LIMITS.address_line}
                autoComplete="off"
                onChange={(event) => set(field)(event.target.value)}
              />
            </Field>
          ))}
          <Field label={strings.customers.form.country} error={fieldProblem("address")}>
            <Combobox
              variant="field"
              value={draft.country}
              onValueChange={set("country")}
              options={COUNTRY_OPTIONS}
              placeholder={strings.customers.form.noCountry}
              searchLabel={strings.customers.form.findCountry}
            />
          </Field>
        </div>
      </fieldset>

      <Field label={strings.customers.form.notes} hint={strings.customers.form.notesHint} error={fieldProblem("notes")}>
        <Textarea
          value={draft.notes}
          rows={6}
          maxLength={COMPANY_LIMITS.notes}
          onChange={(event) => set("notes")(event.target.value)}
        />
      </Field>

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          {strings.customers.form.cancel}
        </Button>
        <Button type="submit" variant="primary" pending={pending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
